import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faArrowRight, faCheck, faX, faChartLine } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/apiService';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface TeamInfo {
  seriesId: number;
  teamId: number;
  active: boolean;
  createdAt: string;
  imageId: number;
  squadId: number;
  teamName: string;
  updatedAt: string;
  id: string;
}

interface SquadInfo {
  seriesId: number;
  squadId: number;
  playerId: number;
  battingStyle: string;
  bowlingStyle: string;
  createdAt: string;
  playerImageId: number;
  playerName: string;
  playerRole: string;
  playerShortRole: string;
  teamId: number;
  updatedAt: string;
  teamInfo: {
    seriesId: number;
    teamId: number;
    active: boolean;
    createdAt: string;
    imageId: number;
    squadId: number;
    teamName: string;
    updatedAt: string;
    id: string;
  };
  id: string;
}

interface VenueInfo {
  timezone: string;
}

interface MatchInfo {
  seriesId: number;
  matchId: number;
  matchFormat: string;
  matchType: string;
  status: string;
  venue: string;
  matchDate: string;
  startDate: string;
  result: string;
  team1Id: number;
  team2Id: number;
  createdAt: string;
  updatedAt: string;
  id: string;
  venueInfo?: VenueInfo;
}

interface Series {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  status: 'ongoing' | 'completed' | 'upcoming';
  teams: string[];
  format: string;
  description?: string;
  published: boolean;
  matchCount: number;
  teamCount: number;
  type: string;
  active: boolean;
  teamInfo?: TeamInfo[];
  squadInfo?: SquadInfo[];
  matchInfo?: MatchInfo[];
}

const SeriesListAG: React.FC = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [forceRefreshing, setForceRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [fetchingData, setFetchingData] = useState<Record<number, {teams: boolean, squads: boolean, matches: boolean}>>({});
  const [typeFilter, setTypeFilter] = useState('');
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async (force: boolean = false) => {
    try {
      setLoading(true);
      const response = await apiService.getSeries(force);
      const apiSeries = response?.data || response || [];
      
      const convertedSeries: Series[] = apiSeries.map((s: any) => {
        // Get start date with time from first match if available, otherwise from series info
        let startDate = '';
        if (s.matchInfo && s.matchInfo.length > 0 && s.matchInfo[0].startDate) {
          startDate = s.matchInfo[0].startDate;
        } else if (s.startDate) {
          startDate = s.startDate;
        }

        const endDate = s.endDate ? new Date(s.endDate).toISOString().split('T')[0] : '';

        // Determine status: use API status field if published, otherwise calculate from dates
        let status: 'upcoming' | 'ongoing' | 'completed' = 'upcoming';
        if (s.publish && s.status) {
          // Published series: use the status from the API
          const apiStatus = s.status.toLowerCase();
          if (apiStatus === 'ongoing' || apiStatus === 'live') {
            status = 'ongoing';
          } else if (apiStatus === 'completed' || apiStatus === 'ended') {
            status = 'completed';
          } else {
            status = 'upcoming';
          }
        } else {
          // Unpublished series: calculate status from dates
          const now = new Date();
          const seriesStartDate = startDate ? new Date(startDate) : null;
          const seriesEndDate = s.endDate ? new Date(s.endDate) : null;

          if (seriesStartDate && seriesEndDate) {
            if (now < seriesStartDate) {
              status = 'upcoming';
            } else if (now >= seriesStartDate && now <= seriesEndDate) {
              status = 'ongoing';
            } else {
              status = 'completed';
            }
          } else if (seriesStartDate && now >= seriesStartDate) {
            status = 'ongoing';
          }
        }

        return {
          id: s.seriesId || parseInt(s.id),
          name: s.name,
          startDate: startDate,
          endDate: endDate,
          status: status,
          teams: s.teamInfo ? s.teamInfo.map((t: TeamInfo) => t.teamName) : [],
          format: s.format ?? 'unknown',
          description: s.alternateName || '',
          published: s.publish || false,
          matchCount: s.matchInfo ? s.matchInfo.length : 0,
          teamCount: s.teamInfo ? s.teamInfo.length : 0,
          type: s.type,
          active: s.active,
          teamInfo: s.teamInfo || [],
          squadInfo: s.squadInfo || [],
          matchInfo: s.matchInfo || []
        };
      });
      
      setSeries(convertedSeries);
      
      const types = Array.from(new Set(convertedSeries.map(s => s.type).filter(Boolean)));
      setAvailableTypes(types);
      
      setError('');
    } catch (err) {
      setError('Failed to fetch series. Please try again.');
      console.error('Error fetching series:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForceRefresh = async () => {
    try {
      setForceRefreshing(true);
      await fetchSeries(true);
    } finally {
      setForceRefreshing(false);
    }
  };

  const fetchTeams = useCallback(async (seriesId: number) => {
    console.log('🟢 fetchTeams function called with seriesId:', seriesId);
    try {
      setFetchingData(prev => ({ ...prev, [seriesId]: { ...prev[seriesId], teams: true } }));
      console.log('🟢 Calling API service fetchTeamsForSeries...');
      await apiService.fetchTeamsForSeries(seriesId.toString());
      console.log('🟢 API call successful');
      alert('Teams fetched successfully!');
      const response = await apiService.getSeries();
      const apiSeries = response?.data || response || [];
      const foundSeries = apiSeries.find((s: any) => (s.seriesId || parseInt(s.id)) === seriesId);

      if (foundSeries && foundSeries.teamInfo) {
        setSeries(prev => prev.map(s =>
          s.id === seriesId ? {
            ...s,
            teamInfo: foundSeries.teamInfo,
            teamCount: foundSeries.teamInfo.length,
            teams: foundSeries.teamInfo.map((t: TeamInfo) => t.teamName)
          } : s
        ));
      }
    } catch (err: any) {
      console.error('🔴 Error fetching teams:', err);
      alert(`Failed to fetch teams: ${err.message}`);
    } finally {
      setFetchingData(prev => ({ ...prev, [seriesId]: { ...prev[seriesId], teams: false } }));
    }
  }, []);

  const fetchSquads = useCallback(async (seriesId: number) => {
    console.log('🟣 fetchSquads function called with seriesId:', seriesId);
    try {
      setFetchingData(prev => ({ ...prev, [seriesId]: { ...prev[seriesId], squads: true } }));
      console.log('🟣 Calling API service fetchSquadsForSeries...');
      await apiService.fetchSquadsForSeries(seriesId.toString());
      console.log('🟣 API call successful');
      alert('Squads fetched successfully!');
      const response = await apiService.getSeries();
      const apiSeries = response?.data || response || [];
      const foundSeries = apiSeries.find((s: any) => (s.seriesId || parseInt(s.id)) === seriesId);

      if (foundSeries && foundSeries.squadInfo) {
        setSeries(prev => prev.map(s =>
          s.id === seriesId ? { ...s, squadInfo: foundSeries.squadInfo } : s
        ));
      }
    } catch (err: any) {
      console.error('🔴 Error fetching squads:', err);
      alert(`Failed to fetch squads: ${err.message}`);
    } finally {
      setFetchingData(prev => ({ ...prev, [seriesId]: { ...prev[seriesId], squads: false } }));
    }
  }, []);

  const fetchMatches = useCallback(async (seriesId: number) => {
    console.log('🔵 fetchMatches function called with seriesId:', seriesId);
    try {
      setFetchingData(prev => ({ ...prev, [seriesId]: { ...prev[seriesId], matches: true } }));
      console.log('🔵 Calling API service fetchMatchesForSeries...');
      await apiService.fetchMatchesForSeries(seriesId.toString());
      console.log('🔵 API call successful');
      alert('Matches fetched successfully!');
      const response = await apiService.getSeries();
      const apiSeries = response?.data || response || [];
      const foundSeries = apiSeries.find((s: any) => (s.seriesId || parseInt(s.id)) === seriesId);

      if (foundSeries && foundSeries.matchInfo) {
        setSeries(prev => prev.map(s =>
          s.id === seriesId ? { ...s, matchInfo: foundSeries.matchInfo, matchCount: foundSeries.matchInfo.length } : s
        ));
      }
    } catch (err: any) {
      console.error('🔴 Error fetching matches:', err);
      alert(`Failed to fetch matches: ${err.message}`);
    } finally {
      setFetchingData(prev => ({ ...prev, [seriesId]: { ...prev[seriesId], matches: false } }));
    }
  }, []);

  const getFormatColor = (format: string) => {
    switch (format) {
      case 'TEST':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'ODI':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'T20':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ongoing':
        return 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-400 border-orange-300 dark:border-orange-500/30';
      case 'completed':
        return 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 border-green-300 dark:border-green-500/30';
      case 'upcoming':
        return 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-400 border-blue-300 dark:border-blue-500/30';
      default:
        return 'bg-gray-100 dark:bg-dark-300/40 text-gray-800 dark:text-gray-400 border-gray-300 dark:border-dark-300/40';
    }
  };

  // Cell Renderers
  const SeriesNameRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <div>
        <div className="font-semibold text-sm text-gray-900">{params.data.name}</div>
        {params.data.description && (
          <div className="text-xs text-gray-500 mt-0.5">
            {params.data.description}
          </div>
        )}
      </div>
    );
  }, []);

  const FormatRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getFormatColor(params.value)}`}>
        {params.value}
      </span>
    );
  }, []);

  const DurationRenderer = useCallback((params: ICellRendererParams) => {
    const formatDateTime = (dateString: string) => {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);

        const options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZoneName: 'short'
        };

        return date.toLocaleString('en-US', options);
      } catch (error) {
        return dateString;
      }
    };

    const startDate = params.data.startDate;

    return (
      <div className="text-xs">
        <div className="text-gray-900">{formatDateTime(startDate)}</div>
      </div>
    );
  }, []);

  const StatusRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${getStatusColor(params.value)}`}>
        {params.value}
      </span>
    );
  }, []);

  const ActionsRenderer = useCallback((params: ICellRendererParams) => {
    const seriesId = params.data?.id;

    const handleViewClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (seriesId) {
        navigate(`/dashboard/series/${seriesId}`);
      }
    };

    return (
      <div className="flex items-center gap-2 h-full">
        {/* Primary Action - View Details */}
        <button
          onClick={handleViewClick}
          className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
        >
          <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3 mr-1.5" />
          View
        </button>
      </div>
    );
  }, [navigate]);

  const SeriesImageRenderer = useCallback((params: ICellRendererParams) => {
    const seriesId = params.data?.id;
    return (
      <div className="flex items-center justify-center h-full">
        <img
          src={`https://cricclash-s3.s3.us-east-2.amazonaws.com/series/${seriesId}.jpg`}
          alt={params.data?.name || 'Series'}
          className="w-10 h-10 rounded object-cover border border-gray-200 dark:border-dark-300/40"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNiAxMCA1IDIxIj48L3BvbHlsaW5lPjwvc3ZnPg==';
          }}
        />
      </div>
    );
  }, []);

  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: 'image',
      headerName: 'Image',
      width: 80,
      cellRenderer: SeriesImageRenderer,
      sortable: false,
      filter: false,
    },
    {
      field: 'name',
      headerName: 'Series Name',
      width: 300,
      cellRenderer: SeriesNameRenderer,
      autoHeight: true,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'format',
      headerName: 'Format',
      width: 120,
      cellRenderer: FormatRenderer,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'startDate',
      headerName: 'Start Time',
      width: 220,
      cellRenderer: DurationRenderer,
      autoHeight: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: (filterDate: Date, cellValue: string) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          const filterDateTime = filterDate.getTime();
          const cellDateTime = cellDate.getTime();

          if (cellDateTime < filterDateTime) return -1;
          if (cellDateTime > filterDateTime) return 1;
          return 0;
        },
      },
      valueGetter: (params: any) => {
        if (!params.data.startDate) return null;
        return new Date(params.data.startDate);
      },
    },
    {
      field: 'teamCount',
      headerName: 'Teams',
      width: 100,
      cellStyle: { textAlign: 'center' },
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'matchCount',
      headerName: 'Matches',
      width: 110,
      cellStyle: { textAlign: 'center' },
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      cellRenderer: StatusRenderer,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'published',
      headerName: 'Published',
      width: 120,
      cellRenderer: (params: ICellRendererParams) => {
        const published = params.data?.published || false;
        return (
          <div className="flex justify-center items-center h-full">
            {published ? (
              <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-green-600" />
            ) : (
              <FontAwesomeIcon icon={faX} className="h-4 w-4 text-red-600" />
            )}
          </div>
        );
      },
      filter: 'agTextColumnFilter',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      cellRenderer: ActionsRenderer,
      sortable: false,
      filter: false,
      pinned: 'right',
    },
  ], [SeriesImageRenderer, SeriesNameRenderer, FormatRenderer, DurationRenderer, StatusRenderer, ActionsRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
  }), []);

  const filteredSeries = typeFilter
    ? series.filter(s => s.type === typeFilter)
    : series;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-100 dark:bg-primary-500/20 rounded-lg border border-blue-200 dark:border-primary-500/30">
            <FontAwesomeIcon icon={faChartLine} className="h-6 w-6 text-blue-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Cricket Series Management</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage cricket series, teams, squads, and matches
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold">{filteredSeries.length}</span> of{' '}
            <span className="font-semibold">{series.length}</span> series
          </span>
          <button
            onClick={() => fetchSeries(false)}
            disabled={loading || forceRefreshing}
            className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors shadow-sm dark:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faRefresh} className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleForceRefresh}
            disabled={loading || forceRefreshing}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors shadow-sm dark:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faRefresh} className={`h-4 w-4 mr-2 ${forceRefreshing ? 'animate-spin' : ''}`} />
            {forceRefreshing ? 'Refreshing...' : 'Force Refresh'}
          </button>
        </div>
      </div>

      {/* Type Filter Chips */}
      {availableTypes.length > 0 && (
        <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Series Type:</span>
            <button
              onClick={() => setTypeFilter('')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === ''
                  ? 'bg-blue-600 dark:bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-300/60 border border-gray-200 dark:border-dark-300/40'
              }`}
            >
              All Types
            </button>
            {availableTypes.map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                  typeFilter === type
                    ? 'bg-blue-600 dark:bg-primary-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-300/60 border border-gray-200 dark:border-dark-300/40'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/10 border-l-4 border-red-500 dark:border-danger-500 p-4 rounded">
          <div className="text-sm text-red-800 dark:text-danger-500 font-medium">{error}</div>
        </div>
      )}

      {/* AG Grid */}
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
        <div className="ag-theme-material" style={{ height: 'calc(100vh - 450px)', minHeight: '400px', width: '100%' }}>
          <AgGridReact
            rowData={filteredSeries}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            loading={loading}
            rowHeight={60}
            animateRows={true}
            domLayout='normal'
            theme={themeQuartz}
            onFirstDataRendered={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SeriesListAG;
